import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';

import './BindVariablesModal.css'; // CSS 파일도 생성 예정
import { toCssPx } from '../utils/appZoom';
// 형식은 **추론하지 않는다**(2026-10-26 사용자 요청) — 기본은 VARCHAR2, 사람이 고를 때만 NUMBER.
// 숫자처럼 보인다고 NUMBER 로 묶으면 문자 컬럼과 견줄 때 실행계획이 달라진다(실행 결과가 화면의 표시와 갈렸다).
export const BIND_TYPES = ['VARCHAR2', 'NUMBER'];

/**
 * 바인드 변수 입력 — **편집기를 막지 않는다**(2026-10-26): 창을 띄운 채 편집기를 눌러 값을 복사해 붙여 넣을 수 있다.
 * editOnly 면(편집기 오른쪽 바인드 변수 섹션의 「편집」) 값만 저장하고 실행하지 않는다.
 */
const BindVariablesModal = ({ isOpen, statements, onSubmit, onClose, isSpecificStatement = false, specificStatementNumber = null, editOnly = false }) => {
    const { t } = useTranslation();
    const [statementInputValues, setStatementInputValues] = useState({}); // 문장별 바인드 변수 값 저장
    const [statementTypes, setStatementTypes] = useState({}); // 문장별 형식 — 고르지 않으면 VARCHAR2
    const [position, setPosition] = useState({ x: 0, y: 0 }); // 모달 위치 상태
    const [isDragging, setIsDragging] = useState(false);
    const [relPos, setRelPos] = useState({ x: 0, y: 0 }); // 드래그 시작 시 마우스와 모달 좌상단 간의 상대 위치

    const modalRef = useRef(null); // 모달 DOM 요소에 대한 ref

    useEffect(() => {
        if (isOpen && statements) {
            // 모달이 열릴 때, 문장별 바인드 변수에 대한 초기 값 설정
            const initialStatementValues = {};
            const initialTypes = {};
            statements.forEach(stmt => {
                const statementKey = `statement_${stmt.statementIndex}`;
                initialStatementValues[statementKey] = {};
                initialTypes[statementKey] = {};
                stmt.bindVars.forEach(varName => {
                    initialStatementValues[statementKey][varName] = stmt.currentValues[varName] || '';
                    const ty = (stmt.currentTypes || {})[varName];
                    initialTypes[statementKey][varName] = BIND_TYPES.includes(ty) ? ty : 'VARCHAR2';
                });
            });
            setStatementInputValues(initialStatementValues);
            setStatementTypes(initialTypes);

            // 모달이 열릴 때 중앙 정렬
            if (modalRef.current) {
                const modalWidth = modalRef.current.offsetWidth;
                const modalHeight = modalRef.current.offsetHeight;
                setPosition({
                    x: (window.innerWidth - modalWidth) / 2,
                    y: (window.innerHeight - modalHeight) / 2,
                });
            }

        } else {
            setIsDragging(false); // 닫힐 때 드래그 상태 초기화
        }
    }, [isOpen, statements]);

    // onMouseMoveWindow와 onMouseUpWindow 함수를 useEffect 앞으로 이동시키고 useCallback으로 감쌈
    const onMouseMoveWindow = useCallback((e) => {
        if (!isDragging) return;
        setPosition({
            // 화면 확대(utils/appZoom) 중이면 마우스 좌표를 CSS px 로 — offsetLeft 는 CSS px 이다
            x: toCssPx(e.pageX) - relPos.x,
            y: toCssPx(e.pageY) - relPos.y,
        });
        e.stopPropagation();
        e.preventDefault();
    }, [isDragging, relPos]); // isDragging과 relPos를 의존성으로 추가

    const onMouseUpWindow = useCallback((e) => {
        setIsDragging(false);
        e.stopPropagation();
        e.preventDefault();
    }, []); // 이 함수는 외부 상태에 의존하지 않으므로 빈 배열

    // 드래그 이벤트 리스너 등록 및 해제 useEffect
    useEffect(() => {
        if (isDragging) {
            window.addEventListener('mousemove', onMouseMoveWindow);
            window.addEventListener('mouseup', onMouseUpWindow);
        } else {
            window.removeEventListener('mousemove', onMouseMoveWindow);
            window.removeEventListener('mouseup', onMouseUpWindow);
        }
        return () => {
            window.removeEventListener('mousemove', onMouseMoveWindow);
            window.removeEventListener('mouseup', onMouseUpWindow);
        };
    }, [isDragging, onMouseMoveWindow, onMouseUpWindow]); // 이제 이 함수들을 의존성 배열에 추가

    if (!isOpen) {
        return null;
    }

    const handleChange = (statementKey, varName, value) => {
        console.log(`[BindVariablesModal] handleChange - ${statementKey}.${varName}: "${value}"`);
        setStatementInputValues(prev => {
            const updated = {
                ...prev,
                [statementKey]: {
                    ...prev[statementKey],
                    [varName]: value
                }
            };
            console.log(`[BindVariablesModal] handleChange - Updated state:`, JSON.stringify(updated, null, 2));
            return updated;
        });
    };

    const handleSubmit = () => {
        console.log('[BindVariablesModal] handleSubmit - Submitting values:', JSON.stringify(statementInputValues, null, 2));
        onSubmit(statementInputValues, statementTypes);
        onClose(); // 제출 후 모달 닫기
    };

    const handleCancel = () => {
        onClose();
    };

    const onMouseDownHeader = (e) => {
        if (e.button !== 0) return; // 왼쪽 마우스 버튼만
        if (!modalRef.current) return; // modalRef.current가 null일 경우 방지
        setIsDragging(true);
        setRelPos({
            x: toCssPx(e.pageX) - modalRef.current.offsetLeft,
            y: toCssPx(e.pageY) - modalRef.current.offsetTop,
        });
        e.stopPropagation();
        e.preventDefault();
    };

    // 모달 외부 클릭 시 닫기 (선택적 구현)
    const handleBackdropClick = (e) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    return (
        // 바탕은 **눌림을 통과시킨다**(bind-modal-passthrough) — 창을 띄운 채 편집기에서 값을 골라 복사할 수 있게
        <div className="modal-backdrop bind-modal-passthrough" /*onClick={handleBackdropClick} - 헤더 드래그와 충돌 방지 위해 backdrop 클릭 제거 또는 조건부 처리*/>
            <div 
                className="modal-content draggable-modal" 
                ref={modalRef} 
                style={{ left: `${position.x}px`, top: `${position.y}px`, position: 'absolute' /* 드래그를 위해 position 변경 */ }}
            >
                <div className="modal-header" onMouseDown={onMouseDownHeader}>
                    <h2>
                        {isSpecificStatement
                            ? t('common:bv.editForStatement', { n: specificStatementNumber })
                            : t('common:bv.title')
                        }
                    </h2>
                </div>
                <div className="bind-modal-hint">{t('common:bv.copyHint')}</div>
                <div className="statements-list">
                    {statements && statements.map((stmt, stmtIndex) => {
                        const statementKey = `statement_${stmt.statementIndex}`;
                        return (
                            <div key={statementKey} className="statement-section">
                                <h3 className="statement-title">SQL Statement {stmt.statementIndex + 1}</h3>
                                <div className="statement-preview">
                                    {stmt.statementText.length > 100
                                        ? `${stmt.statementText.substring(0, 100)}...`
                                        : stmt.statementText}
                                </div>
                                <div className="variables-list">
                                    {stmt.bindVars.map((varName, varIndex) => {
                                        const currentValue = statementInputValues[statementKey]
                                            ? statementInputValues[statementKey][varName]
                                            : '';
                                        return (
                                            <div key={varName} className="variable-item">
                                                <label htmlFor={`bind-var-${statementKey}-${varName}`}>:{varName}</label>
                                                <input
                                                    type="text"
                                                    id={`bind-var-${statementKey}-${varName}`}
                                                    value={currentValue}
                                                    onChange={(e) => handleChange(statementKey, varName, e.target.value)}
                                                    autoFocus={stmtIndex === 0 && varIndex === 0} // 첫 번째 문장의 첫 번째 변수에 자동 포커스
                                                />
                                                {/* 이 값이 **어떤 타입으로 묶이는지** 보여 준다. 숫자처럼 보이는 문자열을
                                                    VARCHAR2 로 묶으면 암시적 형변환이 일어나 실행계획이 달라진다 —
                                                    실행 전에 눈으로 확인할 수 있어야 한다(요청 2026-09-18). */}
                                                <select
                                                    className="bind-type-select"
                                                    title={t('common:bv.typeHint')}
                                                    aria-label={t('common:bv.typeHint')}
                                                    value={(statementTypes[statementKey] || {})[varName] || 'VARCHAR2'}
                                                    onChange={(e) => setStatementTypes(prev => ({ ...prev, [statementKey]: { ...(prev[statementKey] || {}), [varName]: e.target.value } }))}
                                                >
                                                    {BIND_TYPES.map(ty => <option key={ty} value={ty}>{ty}</option>)}
                                                </select>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        );
                    })}
                </div>
                <div className="modal-actions">
                    <button onClick={handleSubmit} className="submit-button">{editOnly ? t('common:bv.save') : t('common:bv.run')}</button>
                    <button onClick={handleCancel} className="cancel-button">{t('common:bv.cancel')}</button>
                </div>
            </div>
        </div>
    );
};

BindVariablesModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    statements: PropTypes.arrayOf(PropTypes.shape({
        statementIndex: PropTypes.number.isRequired,
        statementText: PropTypes.string.isRequired,
        bindVars: PropTypes.arrayOf(PropTypes.string).isRequired,
        currentValues: PropTypes.object.isRequired
    })), // 문장별 바인드 변수 정보 목록
    onSubmit: PropTypes.func.isRequired, // 사용자가 값을 제출했을 때 호출될 함수
    onClose: PropTypes.func.isRequired, // 모달을 닫을 때 호출될 함수
    isSpecificStatement: PropTypes.bool, // 특정 문장만 편집하는지 여부
    specificStatementNumber: PropTypes.number, // 특정 문장 번호 (1-based)
    editOnly: PropTypes.bool, // 값만 저장(실행하지 않음) — 편집기 오른쪽 바인드 변수 섹션에서 열 때
};

export default BindVariablesModal; 